import { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Music } from 'lucide-react';
import { usePlayer } from '@/context/PlayerContext';
import { cn } from '@/lib/utils';
import { parseLrc, findCurrentLineIndex, type LrcLine, type LrcWord } from '@/utils/lrcParser';

function WordHighlight({ words, currentTime }: { words: LrcWord[]; currentTime: number }) {
  return (
    <>
      {words.map((word, wi) => {
        const nextTimestamp = words[wi + 1]?.timestamp ?? Infinity;
        const isWordActive = currentTime >= word.timestamp && currentTime < nextTimestamp;
        const isWordPast = currentTime >= nextTimestamp;
        return (
          <span
            key={wi}
            className={cn(
              'transition-colors duration-150',
              isWordPast && 'text-zzing-purple-500',
              isWordActive && 'text-zzing-purple-400 font-bold',
            )}
          >
            {word.text}
          </span>
        );
      })}
    </>
  );
}

export function LyricsView() {
  const { currentTrack, currentTime, seek, contentView } = usePlayer();
  const containerRef = useRef<HTMLDivElement>(null);
  const activeLineRef = useRef<HTMLParagraphElement>(null);
  const [lines, setLines] = useState<LrcLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const lastScrollTime = useRef(0);

  // Load and parse LRC content
  useEffect(() => {
    if (!currentTrack?.lrcUrl) {
      setLines([]);
      return;
    }

    const loadLrc = async () => {
      setLoading(true);
      try {
        const response = await fetch(currentTrack.lrcUrl!);
        if (!response.ok) throw new Error('Failed to load');
        const content = await response.text();
        const parsed = parseLrc(content);
        setLines(parsed.lines);
      } catch {
        setLines([]);
      } finally {
        setLoading(false);
      }
    };

    loadLrc();
  }, [currentTrack?.lrcUrl]);

  const currentLineIndex = findCurrentLineIndex(lines, currentTime);

  // Auto-scroll to active line
  useEffect(() => {
    if (!autoScroll || !activeLineRef.current || !containerRef.current) return;
    const now = Date.now();
    if (now - lastScrollTime.current < 2000) return;

    activeLineRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [currentLineIndex, autoScroll]);

  const handleScroll = useCallback(() => {
    lastScrollTime.current = Date.now();
  }, []);

  if (contentView !== 'lyrics') {
    return null;
  }

  if (!currentTrack) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 text-text-muted">
        <Music className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg">Geen nummer aan het afspelen</p>
        <p className="text-sm mt-2">Start een nummer om de tekst te zien</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20">
        <div className="w-8 h-8 border-2 border-zzing-purple-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-text-secondary">Lyrics laden...</p>
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20">
        <div className="bg-bg-elevated rounded-2xl p-8 max-w-md text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-text-muted opacity-50" />
          <h3 className="text-xl font-semibold text-text-primary mb-2">
            Geen songtekst beschikbaar
          </h3>
          <p className="text-text-secondary mb-4">
            Er is nog geen gesynchroniseerde tekst voor dit nummer.
          </p>
          <div className="space-y-2 text-sm text-text-muted">
            <p className="font-medium text-text-primary">{currentTrack.title}</p>
            {currentTrack.trackName && (
              <p>{currentTrack.trackName}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header with track info */}
      <div className="sticky top-0 z-10 p-4 bg-gradient-to-b from-bg-base via-bg-base to-transparent pb-8">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-text-primary">
            {currentTrack.title}
          </h2>
          {currentTrack.trackName && (
            <p className="text-sm text-text-secondary">{currentTrack.trackName}</p>
          )}
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={cn(
              'text-xs px-2 py-1 rounded mt-2 transition-colors',
              autoScroll
                ? 'bg-zzing-purple-500/20 text-zzing-purple-500'
                : 'bg-bg-elevated-hover text-text-secondary'
            )}
          >
            Auto-scroll {autoScroll ? 'aan' : 'uit'}
          </button>
        </div>
      </div>

      {/* Scrollable lyrics */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-6 md:px-12 pb-32"
      >
        <div className="max-w-2xl mx-auto py-8">
          {lines.map((line, i) => {
            const isActive = i === currentLineIndex;
            const isPast = i < currentLineIndex;
            return (
              <p
                key={`${line.timestamp}-${i}`}
                ref={isActive ? activeLineRef : null}
                className={cn(
                  'py-3 text-xl md:text-2xl lg:text-3xl font-medium',
                  'cursor-pointer transition-all duration-300',
                  isActive && 'text-text-primary scale-105 origin-left',
                  isPast && 'text-text-muted',
                  !isActive && !isPast && 'text-text-secondary hover:text-text-primary'
                )}
                onClick={() => seek(line.timestamp)}
              >
                {isActive && line.words ? (
                  <WordHighlight words={line.words} currentTime={currentTime} />
                ) : (
                  line.text || '♪'
                )}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}
